import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import StickyNode from 'react-stickynode';

import { media } from '@mlb/react-patterns/style/helpers/media.ts';
import COLORS from '@mlb/react-patterns/style/universal.color';

import {
    useFixElement,
    useMainContainerQuery,
} from 'bdvp-common/src/hooks';
import { playbackPage, subnav } from 'bdvp-common/src/styles/theme';
import { mainCq } from 'bdvp-common/src/styles/helpers/main-cq';

import { FILTER_BREAKPOINT, FILTER_ANIMATION_SECONDS } from 'bdvp-advanced-search';

const PADDING_TOP_BOTTOM =  playbackPage.padding.top;
const PADDING_LEFT_RIGHT = '32px';
const PLAYLIST_WIDTH = playbackPage.playlist.width;
const PLAYLIST_MARGIN = '54px';
const PLAYER_WIDTH_OFFSET = `${parseFloat(PLAYLIST_WIDTH) + parseFloat(PLAYLIST_MARGIN)}px`;
const PLAYER_WIDTH_OFFSET_100VW = `${parseFloat(PLAYER_WIDTH_OFFSET) + (parseFloat(PADDING_LEFT_RIGHT) * 2)}px`;
const HEADER_HEIGHT = '56px';
const SUBNAV_HEIGHT = '52px';
const VISIBLE_METADATA = '100px';
const VERTICAL_OFFSET = `${parseFloat(HEADER_HEIGHT) + parseFloat(SUBNAV_HEIGHT) + (parseFloat(PADDING_TOP_BOTTOM) * 2)}px`;
const PLAYER_VERTICAL_OFFSET = `${parseFloat(VERTICAL_OFFSET) + parseFloat(VISIBLE_METADATA)}px`;
const STICKY_TOP = parseFloat(PADDING_TOP_BOTTOM) + parseFloat(HEADER_HEIGHT);

const Container = styled.div`
    background: ${COLORS.mlbBlack};
    width: 100%;
    box-sizing: border-box;

    ${mainCq.lg`
        padding: ${PADDING_TOP_BOTTOM} ${PADDING_LEFT_RIGHT};
        width: auto;
        display: flex;
        justify-content: center;
    `}
`;

const MainContent = styled.section`
    top: ${({ theme }) => theme?.subnav?.height};
    background: ${COLORS.mlbBlack};
    z-index: 1;

    ${mainCq.lg`
        flex: 0 0 calc(100% - ${PLAYER_WIDTH_OFFSET});
        width: calc(100% - ${PLAYER_WIDTH_OFFSET});
        min-width: 640px;
        max-width: calc((100vh - ${PLAYER_VERTICAL_OFFSET}) * 1.7777777778); /* height * 16/9 = 16:9 ratio */
        top: auto;

        .vpp-sticky-inner {
            min-width: 640px;
            max-width: calc((100vh - ${PLAYER_VERTICAL_OFFSET}) * 1.7777777778); /* height * 16/9 = 16:9 ratio */
        }

        .active .vpp-sticky-inner {
            width: calc(100% - ${PLAYER_WIDTH_OFFSET_100VW}) !important;
        }

        &.filter-active .active .vpp-sticky-inner {
            width: ${({ theme }) => `calc(100% - ${PLAYER_WIDTH_OFFSET_100VW} - ${theme?.filter?.width}) !important;`}
        }

        .released .vpp-sticky-inner {
            width: 100% !important;
        }
    `}
`;

const PlayerContainer = styled.div`
    padding-bottom: 56.25%; /* for 16:9 */
    width: 100%;
    height: 0;

    ${mainCq.lg`
        position: relative;
        padding-bottom: unset;
        height: calc((100vw - ${PLAYER_WIDTH_OFFSET_100VW}) * 0.5625);
        max-height: calc(100vh - ${PLAYER_VERTICAL_OFFSET});
        min-height: 360px;
    `}

    .filter-active & {
        ${mainCq.lg`
            height: ${({ theme }) => `calc((100vw - ${PLAYER_WIDTH_OFFSET} - ${theme?.filter?.width} - ${parseFloat(PADDING_LEFT_RIGHT) * 2}px) * 0.5625)`};
        `}
    }
`;

const defaultPositionStyles = 'position: relative; top: auto; width: 100%;';
const PlayerWrapper = styled.div`
    z-index: 3;
    width: 100%;
    transition: ${({ isFilterActive }) => isFilterActive ? 'none' : `width ${FILTER_ANIMATION_SECONDS}s ease-in`};

    ${({ isFixed , theme }) => (isFixed && `
        position: fixed;
        top: ${theme?.subnav?.height};
    `)}

    ${media.lgmin`
        top: ${({ isFixed, theme }) => isFixed ? (theme?.subnav?.height) : '0'};
    `}

    ${media[`${FILTER_BREAKPOINT}min`]`
        width: ${(props) => (props.isFilterActive && props.isFixed) ?`calc(100% - ${props.theme?.filter?.width});`: '100%;'};
    `}

    @media screen and (max-height: 499px) {
        ${defaultPositionStyles}
    }

    ${mainCq.md`
        @media screen and (max-height: 999px) {
            ${defaultPositionStyles}
        }
    `}

    ${mainCq.lg`
        ${defaultPositionStyles}
        transition: width ${FILTER_ANIMATION_SECONDS}s ease-in;
    `}

`;

const MetadataContainer = styled.div`
    position: relative;
    margin: 19px 10px;

    ${mainCq.md`
        margin: 21px 16px;
        min-height: 116px;
    `}

    ${mainCq.lg`
        margin: 32px 0 0;
        padding-bottom: 32px;
    `}
`;

const PlaylistContent = styled.aside`
    ${mainCq.md`
        margin-left: 0;
        max-width: 100%;
    `}

    ${mainCq.lg`
        flex: 1 1 auto;
        margin-left: ${PLAYLIST_MARGIN};
        width: 100%;
        max-width: ${PLAYLIST_WIDTH};
        background: ${COLORS.mlbBlack};
        z-index: 3;
    `}
    .p-ad {
        .ad-responsive-size > div:not(:empty) {
            margin-bottom: 19px !important;
            margin-left: 10px !important;
            text-align: left !important;
            ${mainCq.md`
                margin-bottom: 21px !important;
                margin-left: 16px !important;
                text-align: left !important;
            `}
            ${mainCq.lg`
                margin-bottom: 22px !important;
                margin-right: auto !important;
                margin-left: auto !important;
                text-align: left !important;
            `}
        }
    }
`;

const Template = ({
    player,
    metadata,
    playlist,
    companionAd,
    bannerAd,
    notification,
    isFilterActive,
    resultsLink,
}) => {

    const playerContainerRef = useRef(null);

    const matches = useMainContainerQuery();

    const isLargeBreakpoint = matches['LG'];

    const isPlayerFixed = useFixElement(
        playerContainerRef,
        parseFloat(subnav.height),
        isLargeBreakpoint
    );

    return (
        <>
            {bannerAd}
            <Container>
                <MainContent className={isFilterActive ? 'filter-active' : ''}>
                    {!isLargeBreakpoint && notification}
                    <StickyNode
                        enabled={isLargeBreakpoint}
                        top={STICKY_TOP}
                        bottomBoundary={`.${MainContent.styledComponentId}`}
                        innerClass={'vpp-sticky-inner'}
                    >
                        {resultsLink}
                        <PlayerContainer ref={playerContainerRef} isFilterActive={isFilterActive}>
                            <PlayerWrapper isFilterActive={isFilterActive} isFixed={isPlayerFixed}>
                                {player}
                            </PlayerWrapper>
                        </PlayerContainer>
                        <MetadataContainer>
                            {metadata}
                        </MetadataContainer>
                    </StickyNode>
                </MainContent>
                <PlaylistContent>
                    {isLargeBreakpoint && notification}
                    {companionAd}
                    {playlist}
                </PlaylistContent>
            </Container>
        </>
    );

};

Template.propTypes = {
    metadata: PropTypes.element.isRequired,
    player: PropTypes.element.isRequired,
    playlist: PropTypes.element.isRequired,
    companionAd: PropTypes.element,
    bannerAd: PropTypes.element,
    notification: PropTypes.element,
    isFilterActive: PropTypes.bool.isRequired,
    resultsLink: PropTypes.element,
};

Template.defaultProps = {
    companionAd: undefined,
    bannerAd: undefined,
    notification: undefined,
    resultsLink: null,
};

export default Template;
